import EventEmitter from "eventemitter3";
import { EventTypes } from "../core/event-bus";
import { Modals, Pages } from "../types/enums";
import { Page } from "../components/pages/page";
import { Modal } from "../components/modals/modal";

type ModalMap = Record<Modals, Modal | (() => Modal)>;

export class Router {
    private readonly _eventBus: EventEmitter;
    private readonly _routes: Partial<Record<Pages, Page>>;
    private readonly _modals: Partial<ModalMap>;
    private readonly _disableUrlNavigation: boolean;
    private static _route: Pages;
    private static _modal: Modals | null = null;

    constructor(eventBus: EventEmitter, routes: Partial<Record<Pages, Page>>, modals: Partial<ModalMap>, options?: { disableUrlNavigation?: boolean }) {
        this._eventBus = eventBus;
        this._routes = routes;
        this._modals = modals;
        this._disableUrlNavigation = options?.disableUrlNavigation ?? false;
        Router._route = Pages.LANDING;
    }

    public init() {
        this.forPages(page => page.init());
        this.forModals(popup => popup.init());
        this._eventListeners();
    }

    public static get path() {
        return Router._route;
    }

    public readonly forPages = (fn: (_: Page) => void): void => {
        Object.keys(Pages).forEach(page => {
            const route = this._routes[Pages[page as keyof typeof Pages]];
            if (route) fn(route);
        });
    }

    public readonly forModals = (fn: (_: Modal) => void): void => {
        Object.keys(Modals).forEach(key => {
            const modalEnumValue = Modals[key as keyof typeof Modals];
            const modalInstance = this._resolveModal(modalEnumValue);
            if (modalInstance)
                fn(modalInstance);
        });
    }

    private readonly _eventListeners = (): void => {
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages, options: any) => {
            this._eventBus.emit(EventTypes.MODAL_CLOSE, Router._modal);

            if (options?.back)
                this._update(page);
            else
                this._navigate(page);                                                                                                                        
        });

        this._eventBus.on(EventTypes.MODAL_CLOSED, () => { Router._modal = null; });

        window.addEventListener("popstate", () => {
            if (this._disableUrlNavigation) return;
            this._eventBus.emit(EventTypes.MODAL_CLOSE, Router._modal);
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, window.location.pathname as Pages, true)
        });

        this._eventBus.on(EventTypes.MODAL_OPEN, this._show)

        this._eventBus.on(EventTypes.MODAL_CLOSE, (modal: Modals) => {
            this._resolveModal(modal)?.close();
        })
    }

    private _resolveModal(modal: Modals | null): Modal | null {
        if (!modal)
            return null;

        const ref = this._modals[modal];
        return typeof ref === "function" ? ref() : ref ?? null;
    }

    private readonly _update = (page: Pages): void => {
        this._routes[Router.path]?.hide();
        Router._route = this._routes[page] ? page : Pages.LANDING;
        this._routes[Router.path]?.show();

        // Reset page back up to top
        const mainElement = document.querySelector("body");
        if (mainElement)
            mainElement.scrollTop = 0;
    }

    private readonly _navigate = (page: Pages): void => {
        const skipPage = [Pages.CURBSIDE, Pages.UNSUBSCRIBE];
        if (!this._disableUrlNavigation && !skipPage.includes(page)) {
            let urlStr = '';
            if (window.location.search && [Pages.LANDING, Pages.MENU].includes(page)) {
                const url = new URL(page, window.location.origin);
                url.search = window.location.search;
                url.hash = window.location.hash;
                urlStr = url.toString();
            } else {
                urlStr = window.location.origin + page;
            }
            window.history.pushState({}, "", urlStr);
        }
        this._update(page)
    }

    private readonly _show = (modal: Modals): void => {
        const current = this._resolveModal(Router._modal);
        current?.close();
        Router._modal = modal;

        const instance = this._resolveModal(Router._modal);
        instance?.open();

        // Reset modal back up to top
        const modalElement = document.getElementById(instance?.id ?? "");
        if (modalElement)
            modalElement.scrollTop = 0;
    }
}